import {
  ContentCopy,
  Visibility,
  VisibilityOff,
  Warning,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import * as styles from './CredentialCard.styles';

export interface CredentialCardProps {
  credential: {
    id: number;
    displayName: string;
    apiKey: string;
    apiSecret: string;
    businessModel?: string | null;
  };
  environment: 'production' | 'development';
}

export const CredentialCard = ({ credential, environment }: CredentialCardProps) => {
  const theme = useTheme();
  const [showApiToken, setShowApiToken] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const maskedToken = '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022';
  const maskedSecret = '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022';

  const isProduction = environment === 'production';
  const chipColor = isProduction ? '#f44336' : '#ff9800';
  const chipLabel = isProduction ? 'Production' : 'Development';
  const shieldIcon = isProduction
    ? (theme.palette.mode === 'dark' ? '/APIShieldProductionDark.svg' : '/APIShieldProductionLight.svg')
    : (theme.palette.mode === 'dark' ? '/APIShieldDevelopmentDark.svg' : '/APIShieldDevelopmentLight.svg');
  const environmentText = isProduction
    ? 'Use these keys in your live application'
    : 'Use these keys for testing and development';

  return (
    <Card sx={styles.card}>
      <CardContent sx={styles.cardContent}>
        <Box sx={styles.headerContainer}>
          <Box
            component="img"
            src={shieldIcon}
            alt={`${environment} Shield`}
            sx={styles.shieldIcon}
          />
          <Box sx={styles.contentContainer}>
            <Typography
              variant="h6"
              color="text.primary"
              sx={styles.title}
            >
              {isProduction ? 'Production Environment' : 'Development Environment'}
            </Typography>
            {credential.businessModel && (
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={styles.businessModel}
              >
                Business Model: {credential.businessModel}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {environmentText}
            </Typography>
          </Box>
          <Chip
            label={chipLabel}
            size="small"
            sx={isProduction ? styles.productionChip : styles.developmentChip}
          />
        </Box>

        <Box sx={styles.fieldContainer}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={styles.fieldLabel}
          >
            API Token
          </Typography>
          <TextField
            fullWidth
            value={showApiToken ? credential.apiKey : maskedToken}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Box sx={styles.buttonContainer}>
                  <IconButton
                    size="small"
                    onClick={() => setShowApiToken(!showApiToken)}
                    sx={styles.iconButton}
                  >
                    {showApiToken ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleCopyToClipboard(credential.apiKey)}
                    sx={styles.iconButton}
                  >
                    <ContentCopy />
                  </IconButton>
                </Box>
              ),
            }}
            sx={styles.textField}
          />
        </Box>

        <Box sx={styles.fieldContainer}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={styles.fieldLabel}
          >
            API Secret
          </Typography>
          <TextField
            fullWidth
            value={showApiSecret ? credential.apiSecret : maskedSecret}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <Box sx={styles.buttonContainer}>
                  <IconButton
                    size="small"
                    onClick={() => setShowApiSecret(!showApiSecret)}
                    sx={styles.iconButton}
                  >
                    {showApiSecret ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleCopyToClipboard(credential.apiSecret)}
                    sx={styles.iconButton}
                  >
                    <ContentCopy />
                  </IconButton>
                </Box>
              ),
            }}
            sx={styles.textField}
          />
        </Box>

        <Alert
          severity={isProduction ? "error" : "warning"}
          icon={isProduction ? <Warning /> : (
            <Box
              component="img"
              src={shieldIcon}
              alt={`${environment} Shield`}
              sx={styles.alertIcon}
            />
          )}
          sx={isProduction ? styles.productionAlert : styles.developmentAlert}
        >
          {isProduction
            ? 'Keep these keys secure and never expose them in client-side code'
            : 'These keys are for testing only and will not process real transactions'
          }
        </Alert>
      </CardContent>
    </Card>
  );
}; 